import dayjs from 'dayjs';
import { Event, ModifiedEvent } from 'types/Event';
import { Form, useFormState } from 'react-final-form';
import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import arrayMutators from 'final-form-arrays';
import FormControl from 'common/components/FormControl/FormControl';
import FormScrollError from 'common/components/FormScrollError/FormScrollError';
import Button from 'common/components/Button/Button';
import { EstablishmentSelect } from 'components/select/EstablishmentSelect/EstablishmentSelect';
import { Establishment } from 'types/Establishment';
import useAuth from 'common/hooks/useAuth';
import { Frequency } from 'rrule';
import {
  frequencyOptions,
  getRecurrenceDescription,
  getRRuleDetails,
  getRRuleExdates,
} from 'common/utils/rrule';

function getValidationErrorMessage(validationErrors: Record<string, string>[] | undefined, t: any) {
  if (!validationErrors) {
    return '';
  }
  const errorMessages = Object.entries(validationErrors).map(([field, errorMessage]) => {
    return `${t(`forms.${field}`)} : ${errorMessage}`;
  });
  return errorMessages.join('\n');
}

const composeValidators =
  (...validators: any[]) =>
  (value: any) =>
    validators.reduce((error, validator) => error || validator(value), undefined);
const isRequired = () => (value: any) => value ? undefined : 'Requis';
const isDateAfter = (otherDate: string | Date) => (value: string | Date) =>
  value && dayjs(value).isAfter(dayjs(otherDate)) ? undefined : 'Date invalide';
const isDateBefore = (otherDate: string | Date) => (value: string | Date) =>
  value && dayjs(value).isBefore(dayjs(otherDate)) ? undefined : 'Date invalide';

function FormChangeHandler({
  event,
  onChangeEvent,
}: {
  event: Event | null;
  onChangeEvent: (event: ModifiedEvent) => void;
}) {
  const { dirty, values } = useFormState({ subscription: { dirty: true, values: true } });
  const { t } = useTranslation();

  useEffect(() => {
    // Only update if the form is dirty (has changes)
    if (event && dirty) {
      const updateEvent = async () => {
        const updatedEvent = {
          ...event, // Start with original event
          ...values, // Apply form values
          isModified: true,
          title: values.title || '',
          mediatorOnEstablishment: values.mediatorOnEstablishmentObj
            ? {
                id: values.mediatorOnEstablishmentObj.value,
                name: values.mediatorOnEstablishmentObj.label,
              }
            : values.mediatorOnEstablishment || null,
        };

        // Call onChangeEvent with merged values
        onChangeEvent(updatedEvent);
      };

      updateEvent();
    }
  }, [dirty, values, event, onChangeEvent, t]);

  return null; // This component doesn't render anything
}

export interface EventFormProps {
  event: Event | null;
  recurrenceEditMode: 'all' | 'future' | 'single' | null;
  newEventType: 'availability' | 'personal' | 'meeting'; // No meeting creation from BO
  onSubmit: (values: any) => Promise<any>;
  onCancel: () => void;
  onChangeEvent: (event: ModifiedEvent) => void;
}

/**
 * Composant de formulaire pour la création et la modification d'événements
 */
function EventForm({
  event,
  newEventType,
  onChangeEvent,
  onSubmit,
  onCancel,
  recurrenceEditMode,
}: EventFormProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [errorMessage, setErrorMessage] = useState('');
  const [formKey, setFormKey] = useState<string>(`form-${Date.now()}`);
  const isAvailability = useMemo(
    () => event?.eventType === 'availability' || newEventType === 'availability',
    [event?.eventType, newEventType]
  );

  // Mettre à jour la clé du formulaire quand l'événement change
  useEffect(() => {
    setFormKey(`form-${event?.id || 'new'}-${Date.now()}`);
    setErrorMessage('');
  }, [event]);

  const initialValues = useMemo(() => {
    if (event) {
      const { id, title, start, parentStart, end, parentEnd, allDay, rrule, ...rest } = event;
      const isRecurring = !!rrule;

      // Convertir la chaîne RRule en RRuleSet si elle existe
      let rruleString: string | null = null;
      if (isRecurring && rrule) {
        rruleString = rrule;
      }

      const {
        frequency,
        interval,
        repeatFrom: originalRepeatFrom,
        repeatUntil,
      } = getRRuleDetails(rruleString || '');

      const repeatFrom =
        recurrenceEditMode === 'future' ? dayjs(start).toDate() : originalRepeatFrom;

      return {
        ...rest,
        id,
        title,
        start: recurrenceEditMode === 'all' ? parentStart || start : start,
        end: recurrenceEditMode === 'all' ? parentEnd || end : end,
        allDay,
        isRecurring,
        frequency: frequency || Frequency.WEEKLY,
        interval: interval || 1,
        repeatFrom: repeatFrom || dayjs(start).toDate(),
        repeatUntil: repeatUntil || null,
        mediatorOnEstablishment: event.mediatorOnEstablishment?.id,
      };
    }

    return {
      title: isAvailability ? t('agenda.newAvailabilityTitle') : '',
      start: dayjs().toDate(),
      end: dayjs().add(1, 'hour').toDate(),
      allDay: true,
      isRecurring: false,
      frequency: Frequency.WEEKLY,
      interval: 1,
      repeatFrom: dayjs().toDate(),
      repeatUntil: null,
      mediatorOnEstablishment: null,
    };
  }, [event, isAvailability, t, recurrenceEditMode]);

  const onSubmitWrapper = async (values: any) => {
    try {
      setErrorMessage(''); // Réinitialiser le message d'erreur avant la soumission

      // Générer la chaîne RRule si l'événement est récurrent
      if (values.isRecurring) {
        const res = await onSubmit({
          ...values,
          // la rrule est ajoutée par l'API
        });

        if (res?.message) {
          setErrorMessage(res.message);
        } else {
          // Fermer le drawer après une soumission réussie
          onCancel();
          setFormKey(`form-success-${Date.now()}`);
        }
      } else {
        const res = await onSubmit(values);

        if (res?.message) {
          setErrorMessage(res.message);
        } else {
          // Fermer le drawer après une soumission réussie
          onCancel();
          setFormKey(`form-success-${Date.now()}`);
        }
      }
    } catch (error: any) {
      setErrorMessage(error.message || 'Une erreur est survenue');
    }
  };

  const excludedDates = event?.rrule ? getRRuleExdates(event.rrule) : [];

  return (
    <Form
      onSubmit={onSubmitWrapper}
      initialValues={initialValues}
      mutators={{
        ...arrayMutators,
        setFieldValue: ([field], state, utils) => {
          utils.changeValue(state, field, () => null);
        },
      }}
      subscription={{
        values: true,
        submitting: true,
      }}
      render={({ handleSubmit, values, form, submitting }) => {
        return (
          <form onSubmit={handleSubmit} noValidate>
            <FormChangeHandler event={event} onChangeEvent={onChangeEvent} />
            <div className="flex flex-row items-center w-full">
              <h2 className="text-xl text-center font-semibold w-full">
                {isAvailability
                  ? event?.id
                    ? t('agenda.editAvailability')
                    : t('agenda.newAvailability')
                  : event?.id
                  ? t('agenda.editEvent')
                  : t('agenda.newEvent')}
              </h2>
            </div>

            {(errorMessage || form.getState().errors?.length > 0) && (
              <div
                className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mt-4"
                role="alert"
              >
                <span className="block sm:inline" style={{ whiteSpace: 'pre-line' }}>
                  {errorMessage ||
                    getValidationErrorMessage(
                      form.getState().errors as Record<string, string>[] | undefined,
                      t
                    )}
                </span>
              </div>
            )}

            <FormScrollError />

            <div className="white-box mt-4">
              <div className={`flex flex-col gap-3 ${isAvailability ? 'mt-3' : ''}`}>
                {!isAvailability && (
                  <FormControl
                    name="title"
                    titleKey="forms.title"
                    required
                    containerStyle="flex flex-col w-full mb-2"
                    className="text-input"
                    validate={isRequired()}
                    placeholder={t('common.newEvent')}
                  />
                )}

                <div className="flex flex-row gap-2">
                  <FormControl
                    name="start"
                    titleKey="forms.start"
                    required
                    type="date"
                    containerStyle="flex flex-col w-1/2 mb-2"
                    className="text-input"
                    validate={composeValidators(isRequired(), isDateBefore(values.end))}
                    timeStep={15}
                  />
                  <FormControl
                    name="end"
                    titleKey="forms.end"
                    required
                    type="date"
                    containerStyle="flex flex-col w-1/2 mb-2"
                    className="text-input"
                    validate={composeValidators(isRequired(), isDateAfter(values.start))}
                    timeStep={15}
                  />
                </div>

                <FormControl
                  name="allDay"
                  titleKey="forms.allDay"
                  type="checkbox"
                  containerStyle="flex flex-row-reverse justify-start items-center w-full"
                  inputContainerStyle=" mx-2"
                  className="text-input"
                />
                {isAvailability && (
                  <FormControl
                    type="custom"
                    name="mediatorOnEstablishment"
                    titleKey="forms.establishment"
                    required
                    containerStyle="flex flex-col w-full mb-2"
                    className="text-input"
                    validate={isRequired()}
                    onChange={(value: any, ...rest) => {
                      form.change('mediatorOnEstablishment', value);
                      // Si nous recevons deux paramètres (id et establishment)
                      if (rest.length > 0) {
                        form.change('mediatorOnEstablishmentObj', rest[0]);
                      }
                    }}
                  >
                    <EstablishmentSelect
                      filterOn={{
                        id: {
                          $in: user?.operator?.establishments?.map((e: Establishment) => e.id),
                        },
                      }}
                      key={`${formKey}-establishment-select`}
                    />
                  </FormControl>
                )}

                <FormControl
                  name="description"
                  titleKey="forms.description"
                  type="textarea"
                  containerStyle="flex flex-col w-full mb-2"
                  className="text-input"
                  rows={4}
                />

                {/* Champs de récurrence pour les événements de disponibilité */}
                {isAvailability && recurrenceEditMode !== 'single' && (
                  <div className="flex flex-col gap-3 mt-2 border-t pt-3">
                    <div
                      className="flex flex-row items-center gap-2 w-full cursor-pointer"
                      onClick={() => form.change('isRecurring', !values.isRecurring)}
                    >
                      <FormControl
                        name="isRecurring"
                        title={`${t('forms.isRecurring')} ${
                          values.isRecurring
                            ? getRecurrenceDescription({
                                frequency: values.frequency,
                                interval: values.interval,
                                from: values.repeatFrom,
                                until: values.repeatUntil,
                              })
                            : '?'
                        }`}
                        type="switch"
                        containerStyle="flex flex-row-reverse justify-end items-center w-full"
                        inputContainerStyle="mx-4"
                        className="text-input"
                      />
                    </div>

                    {values.isRecurring && (
                      <div className="flex flex-col gap-2">
                        <div className="flex flex-row items-center gap-2 w-full">
                          <span className="text-sm">{t('common.every')}</span>
                          <div className="w-16">
                            <FormControl
                              name="interval"
                              type="number"
                              containerStyle="mb-0"
                              className="text-input"
                              validate={composeValidators(isRequired(), (value: number) =>
                                value > 0 ? undefined : 'Intervalle invalide'
                              )}
                            />
                          </div>
                          <div className="flex-1">
                            <FormControl
                              name="frequency"
                              type="select"
                              containerStyle="mb-0"
                              className="text-input"
                              validate={isRequired()}
                              options={frequencyOptions}
                            />
                          </div>
                        </div>

                        <div className="flex items-center">
                          <span className="text-sm mr-2 w-1/2 text-right">{t('common.from')}</span>
                          <div className="flex-1">
                            <FormControl
                              name="repeatFrom"
                              type="date"
                              containerStyle="mb-0"
                              className="text-input"
                              validate={(value) => (value ? isRequired()(value) : undefined)}
                              showTimeSelect={false}
                            />
                          </div>
                        </div>

                        <div className="flex items-center">
                          <span className="text-sm mr-2 w-1/2 text-right">{t('common.until')}</span>
                          <div className="flex-1">
                            <FormControl
                              name="repeatUntil"
                              type="date"
                              containerStyle="mb-0"
                              className="text-input"
                              validate={(value) =>
                                value ? isDateAfter(values.start)(value) : undefined
                              }
                              showTimeSelect={false}
                            />
                          </div>
                        </div>
                        <div className="flex flex-row gap-4 items-start w-full pt-2">
                          <p className="text-sm text-gray-500">
                            <span className="font-medium">
                              {t(
                                excludedDates.length > 1
                                  ? 'agenda.excludedDates'
                                  : 'agenda.excludedDate'
                              )}
                            </span>{' '}
                            {excludedDates.map((date) => dayjs(date).format('DD MMM')).join(', ')}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-row justify-end gap-2 mt-4">
              <Button onClick={onCancel} className="bg-gray-200 text-gray-800" title="Annuler" />
              <Button
                className="bg-primary text-white"
                disabled={submitting || !form.getState().valid}
                onClick={handleSubmit}
                icon="CheckIcon"
                title={t('common.send')}
                type="valid"
              />
            </div>
          </form>
        );
      }}
    />
  );
}

export default EventForm;
